import type { BlockAnimation, BlockType } from '@matter_extractor/shared';

/**
 * L'accord entre les fichiers de l'éditeur de page (refonte modulaire §9.2,
 * §9.3; issue #58).
 *
 * Every `data-testid` more than one file reads is declared HERE, exactly as
 * `experiences/contracts.ts` and `protocoles/contracts.ts` do for their own
 * sub-trees and for the same reason: this screen is nineteen files, and two
 * suites that do not compile it (`e2e/tests/page-editor.spec.ts` and
 * `e2e/screenshots-editeur-de-page.mjs`) recopy these strings. A mark renamed
 * on one side has to turn something red on the other.
 *
 * The prefix is `page-` and not `experience-`, which is deliberate: the screen
 * is the experience's editor in its solo logement, so the marks the versioned
 * layer emits are still `experience-...` (the head, the pastilles, the save
 * box). Reading a testid therefore says which of the two wrote it.
 *
 * What is NOT here: the reader's own marks (`lecteur-...`,
 * `components/reader/contracts.ts`), which the preview mounts as they are.
 */
export const PAGE_EDITOR_IDS = {
  /** The screen's root, kept from the seam of #55 so the older spec still finds it. */
  root: 'experience-page-editeur',

  /** The head's format toggle, and the two segments it holds. */
  format: 'page-format',

  /** The navigation banner: the two neighbours, the position, the transition. */
  nav: 'page-navigation',
  navPrevious: 'page-precedente',
  navNext: 'page-suivante',
  navPosition: 'page-position',
  navTransition: 'page-transition',
  /** Les deux « + » d'insertion du bandeau (§9.3, revue du 2026-09-11). */
  insertBefore: 'page-inserer-avant',
  insertAfter: 'page-inserer-apres',

  /**
   * La page protocole DANS cet éditeur (§9.5): sa carte en lecture seule.
   *
   * What « Remplacer » opens is `timeline/protocol-picker.tsx`, which emits its
   * own `experience-protocole-...` marks: the selector is ONE component and one
   * gesture, mounted from two screens, so renaming a mark of it has to redden
   * both. Only the panel around it belongs to this screen, so only the panel is
   * named here.
   */
  protocolPanel: 'page-protocole',
  /**
   * Le Contexte de §9.5 (grill Variables, D2), sous le sélecteur de protocole :
   * le champ peint, son `{ }` et son compteur. It is `content.context` of the
   * experience, edited here because the protocol page is its one address.
   */
  context: 'page-contexte',
  contextInsert: 'page-contexte-variable',
  contextCount: 'page-contexte-compteur',

  /** The « Page » panel, shown when no block is selected. */
  pagePanel: 'page-panneau',
  pageName: 'page-nom',
  pageDuration: 'page-duree',
  /** « Phrase de remerciement » of the feedback page (retour-experience.md §7.2), and its counter. */
  pageThanks: 'page-remerciement',
  pageThanksCount: 'page-remerciement-compteur',

  /**
   * « Jouer dans la conversation » (prolongation.md §3, §6, issue #186) : la case
   * sous le nom de la page, la raison qui la grise, le « Contexte servi à
   * l'interviewer » et son compteur, et la phrase qui dit que la durée et la
   * transition sont partis. `page-contexte` est celui de la page PROTOCOLE : ce
   * champ-ci est une zone à part, donc une marque à part.
   */
  conversationBox: 'page-conversation',
  conversationReason: 'page-conversation-raison',
  conversationContext: 'page-conversation-contexte',
  conversationContextInsert: 'page-conversation-contexte-variable',
  conversationContextCount: 'page-conversation-contexte-compteur',
  conversationNoTiming: 'page-conversation-sans-minutage',
  /** The sentence under the transition kept by the LAST page of the thread (§3.3). */
  conversationTransitionOut: 'page-conversation-transition-sortie',

  /** The stack: one separator per temps, one row per block. */
  stack: 'page-pile',
  tempsSeparator: 'page-temps',
  blockRow: 'page-bloc',
  /** « masqué dans le fil » sur la ligne d'un Texte, d'une Image ou d'un Son d'une page du fil (§3.3). */
  blockHiddenMark: 'page-bloc-masque',
  /** « ignoré dans le fil » sur le séparateur d'un temps d'une page du fil (§3.3). */
  tempsIgnoredMark: 'page-temps-ignore',
  blockGrip: 'page-bloc-poignee',
  /** L'icône de copie de §4.2, dans la gouttière de la ligne (#118). */
  blockDuplicate: 'page-bloc-dupliquer',
  /**
   * L'icône d'animation de la ligne, devenue un GESTE (§9.3; issue #139).
   *
   * Elle était « pour mémoire » et ne répondait à rien ; elle porte désormais
   * sa bulle et ouvre l'onglet Animation de son bloc. Distincte d'
   * `animationPick` (`page-animation`), qui est la rangée des huit icônes de
   * l'inspecteur et des pistes : ici il n'y en a qu'une, celle du bloc.
   */
  blockAnimation: 'page-bloc-animation',
  /**
   * La croix de la gouttière, après l'icône de copie (§9.3 ; issue #136).
   *
   * Distincte de `blockRemove` (`page-bloc-supprimer`), qui est le bouton
   * « Supprimer ce bloc » de l'inspecteur : les deux gestes restent, et ce sont
   * deux endroits, donc deux marques.
   */
  blockRemoveRow: 'page-bloc-croix',
  /**
   * Le « + » d'un séparateur et le popover des neuf types qu'il ouvre (§9.3 ;
   * issue #136). La même paire sert au « Ajouter un premier bloc » de la page
   * encore vide, parce que c'est le même composant.
   */
  blockAdd: 'page-bloc-ajouter',
  blockAddMenu: 'page-bloc-ajouter-menu',
  blockAddOption: 'page-bloc-ajouter-type',

  /**
   * « Ajouter » - what is LEFT of the grid: the temps, and nothing else (#136).
   *
   * Les neuf boutons de type sont partis dans le popover ci-dessus, donc
   * `page-ajouter-bloc` n'existe plus : un bloc s'ajoute dans le temps qu'on
   * nomme, jamais « à la fin du dernier » (§9.3).
   */
  addGrid: 'page-ajouter',
  addTemps: 'page-ajouter-temps',

  /** The preview, its two switches and the door to the next page. */
  preview: 'page-apercu',
  previewSelect: 'page-apercu-selection',
  previewReplay: 'page-apercu-rejouer',
  previewNext: 'page-apercu-suivante',
  previewBack: 'page-apercu-retour',

  /** « Régler les temps et animations » and the tracks it opens. */
  tracksToggle: 'page-pistes-bascule',
  tracks: 'page-pistes',
  track: 'page-piste',

  /** The inspector of a block: its three tabs and what each panel holds. */
  inspector: 'page-inspecteur',
  inspectorTab: 'page-inspecteur-onglet',
  /**
   * Le sélecteur de type de l'onglet Contenu, et l'avertissement qui le suit
   * quand le bloc est CITÉ et que le type visé ne produit plus de variable
   * (§9.3 ; issue #136). Le bloc garde son `id`, donc la conversion ne casse
   * une citation que dans ce cas-là - et elle le dit avant, jamais après.
   */
  blockTypePick: 'page-bloc-type',
  blockTypeCited: 'page-bloc-type-cite',
  blockLabel: 'page-bloc-libelle',
  /** « Obligatoire » of a Sélection and of a Note (#176) - the Input's and the Checkbox's carry none. */
  blockRequired: 'page-bloc-obligatoire',
  /** « Au clic » of a Bouton: the segmented choice of its action, `Envoyer le retour` included (#176). */
  blockAction: 'page-bloc-action',
  blockActionHint: 'page-bloc-action-aide',
  /**
   * The options of a Sélection (retour-experience.md §7.2, #176): the list, one
   * row per option - its field, its handle and its cross - « Ajouter une
   * option », and the sentence under the list that says a bound or a duplicate.
   */
  selectionOptions: 'page-selection-options',
  selectionOption: 'page-selection-option',
  selectionOptionHandle: 'page-selection-option-poignee',
  selectionOptionRemove: 'page-selection-option-croix',
  selectionOptionAdd: 'page-selection-option-ajouter',
  selectionOptionsNote: 'page-selection-options-phrase',
  /** The line « Échelle » of the inspector of a Note: five stars, never set (#176). */
  noteScale: 'page-note-echelle',
  /**
   * « Libellé d'attente » d'un Bouton qui mène à la page suivante, et sa phrase
   * (noyau.md §4.6, N85 ; planche `AttenteInspecteurBouton` ; issue #174).
   */
  blockWaitingLabel: 'page-bloc-libelle-attente',
  blockWaitingLabelHint: 'page-bloc-libelle-attente-aide',
  /** L'adresse d'un Lien, et le refus nommé qui s'écrit dessous (§9.2, issue #137). */
  blockUrl: 'page-bloc-url',
  blockUrlRefusal: 'page-bloc-url-refus',
  /** Le texte BRUT d'un Texte copiable - pas `richText`, qui est un arbre (issue #137). */
  blockPlainText: 'page-bloc-texte-brut',
  blockVariable: 'page-bloc-variable',
  variableDuplicate: 'page-bloc-variable-doublon',
  blockRemove: 'page-bloc-supprimer',
  blockTemps: 'page-bloc-temps',
  animationPick: 'page-animation',

  /**
   * The WYSIWYG of a Texte block, its toolbar and its counter. The `@` menu and
   * the pills it inserts are the variable module's (`variables/contracts.ts`,
   * `variable-menu`, `variable-pastille`): one component for five zones, so
   * their marks are not this screen's to name.
   */
  richText: 'page-texte',
  richToolbar: 'page-texte-barre',
  richCount: 'page-texte-compteur',

  /** The media library, on an Image and on a Son. */
  assetPick: 'page-fichier',
  assetPlay: 'page-fichier-ecouter',

  /** The inspector of a temps, opened by a click on a separator. */
  tempsInspector: 'page-temps-inspecteur',
  tempsSpeed: 'page-temps-rapidite',
  tempsStart: 'page-temps-depart',
  tempsDelay: 'page-temps-delai',
  tempsAdd: 'page-temps-ajouter',
  tempsRemove: 'page-temps-supprimer',
  tempsCutOff: 'page-temps-signal',

  /**
   * Les conditions d'affichage (§9.2, H67 à H73 ; issue #150) : la pastille grise
   * d'un bloc conditionnel dans la pile, la section « Conditions » de
   * l'inspecteur - son dépliage, une carte par condition, sa croix, ses valeurs -
   * la palette « Ajouter une condition », et la barre « Simuler » de l'aperçu.
   */
  blockConditionPill: 'page-bloc-condition',
  conditions: 'page-conditions',
  conditionsToggle: 'page-conditions-bascule',
  condition: 'page-condition',
  conditionRemove: 'page-condition-retirer',
  conditionValue: 'page-condition-valeur',
  conditionAdd: 'page-condition-ajouter',
  conditionPalette: 'page-condition-palette',
  conditionOption: 'page-condition-option',
  conditionsExitClear: 'page-conditions-retirer-tout',
  simulation: 'page-apercu-simulation',
  simulationShowAll: 'page-apercu-tous-les-blocs',
  simulationControl: 'page-apercu-simuler',

  /** « Nom de la personne dans Sessions » (§9.2, H75): the box of an Input, and its mark in the stack. */
  personName: 'page-bloc-nom-personne',
  personNameMark: 'page-bloc-nom-personne-marque',

  /**
   * L'inspecteur du bloc Noyau (noyau.md §3.2, maquette `EditeurPageNoyau`,
   * issue #151) : le mode, la version des textes (latest ou épinglée) et l'état de leurs versions, la
   * porte vers Réglages > Noyaux, « Aperçu avec » et ses deux listes, la note
   * de la sortie ; et, au-dessus de l'aperçu, la ligne de la paire montrée.
   */
  noyauInspector: 'page-noyau',
  noyauMode: 'page-noyau-mode',
  /** « Seuil d'écart » (noyau.md §3.8, planche `EcartInspecteur`, #169): the field, then its sentence. */
  noyauMaxGap: 'page-noyau-seuil',
  noyauMaxGapHint: 'page-noyau-seuil-phrase',
  noyauTextsPin: 'page-noyau-textes-version',
  noyauTextsState: 'page-noyau-textes-etat',
  noyauTextsOpen: 'page-noyau-textes-ouvrir',
  noyauPreviewPrincipal: 'page-noyau-apercu-principal',
  noyauPreviewSecond: 'page-noyau-apercu-second',
  noyauExitNote: 'page-noyau-sortie',
  noyauPreviewPair: 'page-apercu-paire',
} as const;

/**
 * What the editor is pointing at: one block, one temps, or the page itself.
 *
 * Interface state held ABOVE the three zones, the arbitration
 * `protocoles/contracts.ts` states for the step: the stack presses it, the
 * preview presses it, the inspector renders from it, so it can only live above
 * all three. It is projected onto the draft at every render and never repaired
 * by an effect - a block a colleague's gesture removed would otherwise leave
 * the inspector pointing at nothing.
 */
export type PageSelection =
  | { readonly kind: 'page' }
  | { readonly kind: 'block'; readonly blockId: string }
  | { readonly kind: 'temps'; readonly tempsId: string };

export const PAGE_SELECTION: PageSelection = { kind: 'page' };

/** The three tabs of a block's inspector (§9.3, revue du 2026-09-11). */
export type InspectorTab = 'contenu' | 'visuel' | 'animation';

/**
 * The types the grid OFFERS: all eleven - the nine of §9.2, the Noyau block of
 * noyau.md §3.2 included since the lot that plays it (#151), then the Sélection
 * and the Note of retour-experience.md §4 (#176), offered on EVERY page that has
 * a stack: a content page and the feedback page (the protocol page has none).
 * The alias stays so the grid and the words below keep one name for « what
 * « Ajouter » offers ».
 */
export type OfferedBlockType = BlockType;

/**
 * « Ajouter un texte » - the buttons of the grid, in the maquette's order
 * then in the order issues #137, #151 and #176 append to it.
 */
export const ADD_BLOCK_LABEL: Readonly<Record<OfferedBlockType, string>> = {
  text: 'Ajouter un texte',
  image: 'Ajouter une image',
  sound: 'Ajouter un son',
  button: 'Ajouter un bouton',
  input: 'Ajouter un input',
  checkbox: 'Ajouter une checkbox',
  link: 'Ajouter un lien',
  copyable: 'Ajouter un texte copiable',
  noyau: 'Ajouter un noyau',
  selection: 'Ajouter une sélection',
  note: 'Ajouter une note',
};

/**
 * What each type DOES, in the line the maquette `AjouterUnBloc` writes under it
 * - the Noyau's says its three modes. The accessible description of a button
 * of the grid, and the words of its bubble.
 */
export const ADD_BLOCK_HINT: Readonly<Record<OfferedBlockType, string>> = {
  text: 'Texte riche, variables',
  image: 'Depuis la médiathèque, légende',
  sound: 'Part au premier toucher',
  button: 'Page suivante ou temps suivant',
  input: 'Texte ou email, variable',
  checkbox: 'Obligatoire ou non, variable',
  link: 'Ouvre un nouvel onglet, n’avance pas',
  copyable: 'Un texte brut, un bouton qui copie',
  noyau: 'Premier choix, second choix ou portrait',
  selection: 'Sélection : pastilles à choix unique, variable',
  note: 'Note : cinq étoiles, variable de 1 à 5',
};

/**
 * L'ordre de la grille. Les deux blocs de #137 viennent APRÈS les six de la
 * planche, et le Noyau de #151 en dernier (maquette `AjouterUnBloc`), pas rangés
 * par affinité : la grille est en `grid-cols-3`, donc neuf se posent en
 * 3 + 3 + 3, et un opérateur qui connaissait les huit les retrouve exactement
 * où ils étaient. La Sélection et la Note de #176 prennent les places 10 et 11,
 * sur la quatrième ligne (maquette `editeur-page-retour.html`, choix 5).
 */
export const BLOCK_TYPES: readonly OfferedBlockType[] = [
  'text',
  'image',
  'sound',
  'button',
  'input',
  'checkbox',
  'link',
  'copyable',
  'noyau',
  'selection',
  'note',
];

/**
 * The words of the inspector of a Noyau block (noyau.md §3.2, maquette
 * `EditeurPageNoyau`): its three modes, and what each of them shows.
 */
export const NOYAU_MODE_LABEL = {
  'first-choice': 'Premier choix',
  'second-choice': 'Second choix',
  portrait: 'Portrait',
} as const;

/**
 * The eight animations of §9.3, in the order the legend of `ModeAnimation`
 * lists them, each with what it does in three words.
 *
 * « Au survol leur libellé » - so the label is the accessible name of the
 * control AND the text of the bubble of the design system `Tooltip`, the
 * régime every tooltip of this application follows since issue 103.
 */
export const ANIMATION_LABEL: Readonly<Record<BlockAnimation, string>> = {
  none: 'Aucune',
  fade: 'Fondu',
  wipe: 'Volet',
  zoom: 'Zoom',
  'slide-left': 'Glissé depuis la gauche',
  'slide-right': 'Glissé depuis la droite',
  'slide-up': 'Glissé depuis le haut',
  'slide-down': 'Glissé depuis le bas',
};

export const ANIMATION_ORDER: readonly BlockAnimation[] = [
  'none',
  'fade',
  'wipe',
  'zoom',
  'slide-left',
  'slide-right',
  'slide-up',
  'slide-down',
];
